import { Injectable } from '@angular/core';
import { PropertyStateService } from '../state/property-state.service';
import { CalculatorService } from './calculator.service';

@Injectable()
export class OfferFlipCalculatorService {
// Internal storage of Inputs. The equivalently named public setters exist and should be set by the user.
    #commissionForSellingAgentPercent: number = 0;
    #estimatedRenovationCosts: number = 0;
    #arv: number = 0;
    #desiredInvestorROIPercent: number = 0;
    #holdTimeInMonth: number = 0;
    #estimatedSimoCCPercent: number = 0;
    #lessCommissionPercent: number = 0;
    #annualPropertyTaxes: number = 0;
    #wholeSaleMarkup: number = 0;

    // Outputs (outside of the class they should be treated as readonly)
    #holdingCosts: number = 0;
    #dispositionCosts: number = 0;
    #estimatedSimoCC: number = 0;
    #lessClosingCosts: number = 0;
    #lessCommission: number = 0;
    #propertyTaxesWhileHeld: number = 0;
    #utilAndInsuranceWhileHeld: number = 0;
    #misc: number = 0;
    #profitMargin: number = 0;
    #frontSideCommission: number = 0;
    #totalSBSpread: number = 0;
    #secondSideSalePrice: number = 0;
    #offerPrice: number = 0;

    constructor(
        private state: PropertyStateService,
        private calculator: CalculatorService
    ) {}

    /**
     * Input Setters
     * Each setter will update the output values which depend on it (i.e are derived by it).
     */
    set commissionForSellingAgentPercent(value: number) {
        this.#commissionForSellingAgentPercent = value;

        // Update derived Outputs
        this.frontSideCommission = this.calcFrontSideCommission(this.commissionForSellingAgentPercent, this.offerPrice);
    }

    get commissionForSellingAgentPercent() {
        return this.#commissionForSellingAgentPercent;
    }


    set estimatedRenovationCosts(value: number) {
        this.#estimatedRenovationCosts = value;

        // Update derived Outputs
        this.estimatedSimoCC = this.calcEstimatedSimoCC(
            this.estimatedSimoCCPercent,
            this.holdingCosts,
            this.dispositionCosts,
            this.estimatedRenovationCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );

        this.misc = this.calcMisc(this.estimatedRenovationCosts);

        this.secondSideSalePrice = this.calcSecondSideSalePrice(
            this.estimatedSimoCC,
            this.estimatedRenovationCosts,
            this.dispositionCosts,
            this.holdingCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );
    }

    get estimatedRenovationCosts() {
        return this.#estimatedRenovationCosts;
    }

    set arv(value: number) {
        this.#arv = value;

        // Update derived Outputs
        this.lessClosingCosts = this.calcLessClosingCosts(this.arv);

        this.estimatedSimoCC = this.calcEstimatedSimoCC(
            this.estimatedSimoCCPercent,
            this.holdingCosts,
            this.dispositionCosts,
            this.estimatedRenovationCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );

        this.lessCommission = this.calcLessCommission(this.arv, this.lessCommissionPercent);

        this.profitMargin = this.calcProfitMargin(this.arv, this.desiredInvestorROIPercent);

        this.secondSideSalePrice = this.calcSecondSideSalePrice(
            this.estimatedSimoCC,
            this.estimatedRenovationCosts,
            this.dispositionCosts,
            this.holdingCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );
    }

    get arv() {
        return this.#arv;
    }

    set desiredInvestorROIPercent(value: number) {
        this.#desiredInvestorROIPercent = value;

        // Update derived Outputs
        this.estimatedSimoCC = this.calcEstimatedSimoCC(
            this.estimatedSimoCCPercent,
            this.holdingCosts,
            this.dispositionCosts,
            this.estimatedRenovationCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );

        this.profitMargin = this.calcProfitMargin(this.arv, this.desiredInvestorROIPercent);

        this.secondSideSalePrice = this.calcSecondSideSalePrice(
            this.estimatedSimoCC,
            this.estimatedRenovationCosts,
            this.dispositionCosts,
            this.holdingCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );
    }

    get desiredInvestorROIPercent() {
        return this.#desiredInvestorROIPercent;
    }

    set holdTimeInMonth(value: number) {
        this.#holdTimeInMonth = value;

        // Update derived Outputs
        this.propertyTaxesWhileHeld = this.calcPropertyTaxesWhileHeld(this.annualPropertyTaxes, this.holdTimeInMonth);
        this.utilAndInsuranceWhileHeld = this.calcUtilAndInsuranceWhileHeld(this.holdTimeInMonth);
    }

    get holdTimeInMonth() {
        return this.#holdTimeInMonth;
    }

    set estimatedSimoCCPercent(value: number) {
        this.#estimatedSimoCCPercent = value;

        // Update derived Outputs
        this.estimatedSimoCC = this.calcEstimatedSimoCC(
            this.estimatedSimoCCPercent,
            this.holdingCosts,
            this.dispositionCosts,
            this.estimatedRenovationCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );
    }

    get estimatedSimoCCPercent() {
        return this.#estimatedSimoCCPercent;
    }

    set lessCommissionPercent(value: number) {
        this.#lessCommissionPercent = value;

        // Update derived Outputs
        this.lessCommission = this.calcLessCommission(this.arv, this.lessCommissionPercent);
    }

    get lessCommissionPercent() {
        return this.#lessCommissionPercent;
    }

    set annualPropertyTaxes(value: number) {
        this.#annualPropertyTaxes = value;

        // Update derived Outputs
        this.propertyTaxesWhileHeld = this.calcPropertyTaxesWhileHeld(this.annualPropertyTaxes, this.holdTimeInMonth);
    }

    get annualPropertyTaxes() {
        return this.#annualPropertyTaxes;
    }

    set wholeSaleMarkup(value: number) {
        this.#wholeSaleMarkup = value;

        // Update derived Outputs
        this.totalSBSpread = this.calcTotalSBSpread(this.wholeSaleMarkup, this.frontSideCommission);
        this.offerPrice = this.calcOfferPrice(this.secondSideSalePrice, this.wholeSaleMarkup);
    }

    get wholeSaleMarkup() {
        return this.#wholeSaleMarkup;
    }

    /**
     * Output Setters
     * Each setter will update the other output values which depend on it (i.e are derived by it).
     */
    set holdingCosts(value: number) {
        this.#holdingCosts = value;

        // Update derived Outputs
        this.estimatedSimoCC = this.calcEstimatedSimoCC(
            this.estimatedSimoCCPercent,
            this.holdingCosts,
            this.dispositionCosts,
            this.estimatedRenovationCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );
        this.secondSideSalePrice = this.calcSecondSideSalePrice(
            this.estimatedSimoCC,
            this.estimatedRenovationCosts,
            this.dispositionCosts,
            this.holdingCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );
    }

    get holdingCosts() {
        return this.#holdingCosts;
    }

    set dispositionCosts(value: number) {
        this.#dispositionCosts = value;

        // Update derived Outputs
        this.estimatedSimoCC = this.calcEstimatedSimoCC(
            this.estimatedSimoCCPercent,
            this.holdingCosts,
            this.dispositionCosts,
            this.estimatedRenovationCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );
        this.secondSideSalePrice = this.calcSecondSideSalePrice(
            this.estimatedSimoCC,
            this.estimatedRenovationCosts,
            this.dispositionCosts,
            this.holdingCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );
    }

    get dispositionCosts() {
        return this.#dispositionCosts;
    }

    set estimatedSimoCC(value: number) {
        this.#estimatedSimoCC = value;

        // Update derived Outputs
        this.secondSideSalePrice = this.calcSecondSideSalePrice(
            this.estimatedSimoCC,
            this.estimatedRenovationCosts,
            this.dispositionCosts,
            this.holdingCosts,
            this.arv,
            this.desiredInvestorROIPercent
        );
    }

    get estimatedSimoCC() {
        return this.#estimatedSimoCC;
    }

    set lessClosingCosts(value: number) {
        this.#lessClosingCosts = value;

        // Update derived Outputs
        this.dispositionCosts = this.calcDispositionCosts(this.lessCommission, this.lessClosingCosts);
    }

    get lessClosingCosts() {
        return this.#lessClosingCosts;
    }

    set lessCommission(value: number) {
        this.#lessCommission = value;

        // Update derived Outputs
        this.dispositionCosts = this.calcDispositionCosts(this.lessCommission, this.lessClosingCosts);
    }

    get lessCommission() {
        return this.#lessCommission;
    }

    set propertyTaxesWhileHeld(value: number) {
        this.#propertyTaxesWhileHeld = value;

        // Update derived Outputs
        this.holdingCosts = this.calcHoldingCosts(this.propertyTaxesWhileHeld, this.utilAndInsuranceWhileHeld, this.misc);

    }

    get propertyTaxesWhileHeld() {
        return this.#propertyTaxesWhileHeld;
    }

    set utilAndInsuranceWhileHeld(value: number) {
        this.#utilAndInsuranceWhileHeld = value;

        // Update derived Outputs
        this.holdingCosts = this.calcHoldingCosts(this.propertyTaxesWhileHeld, this.utilAndInsuranceWhileHeld, this.misc);
    }

    get utilAndInsuranceWhileHeld() {
        return this.#utilAndInsuranceWhileHeld;
    }


    set misc(value: number) {
        this.#misc = value;

        // Update derived Outputs
        this.holdingCosts = this.calcHoldingCosts(this.propertyTaxesWhileHeld, this.utilAndInsuranceWhileHeld, this.misc);
    }

    get misc() {
        return this.#misc;
    }


    set profitMargin(value: number) {
        this.#profitMargin = value;

        // Update derived Outputs
        // Noop.
    }

    get profitMargin() {
        return this.#profitMargin;
    }


    set frontSideCommission(value: number) {
        this.#frontSideCommission = value;

        // Update derived Outputs
        this.totalSBSpread = this.calcTotalSBSpread(this.wholeSaleMarkup, this.frontSideCommission);
    }

    get frontSideCommission() {
        return this.#frontSideCommission;
    }


    set totalSBSpread(value: number) {
        this.#totalSBSpread = value;

        // Update derived Outputs
        // Noop
    }

    get totalSBSpread() {
        return this.#totalSBSpread;
    }


    set secondSideSalePrice(value: number) {
        this.#secondSideSalePrice = value;

        // Update derived Outputs
        this.offerPrice = this.calcOfferPrice(this.secondSideSalePrice, this.wholeSaleMarkup);
    }

    get secondSideSalePrice() {
        return this.#secondSideSalePrice;
    }


    set offerPrice(value: number) {
        this.#offerPrice = value;

        // Update derived Outputs
        this.frontSideCommission = this.calcFrontSideCommission(this.commissionForSellingAgentPercent, this.offerPrice);
    }

    get offerPrice() {
        return this.#offerPrice;
    }

    init() {
        this.calculator.setFieldValuesFromParseObject([this.state.offerCalculator_flipCalculationBox, this.state.offerCalculator_flipOfferCalculatorBox], this.state.offerFlip);
    }

    calculate() {
        const state = this.state;

        // Send updated inputs to calculator
        this.commissionForSellingAgentPercent = this.calculator.getField(state.offerCalculator_flipCalculationBox, 'commission').inputValue as number;
        this.arv = this.calculator.getField(state.offerCalculator_flipCalculationBox, 'arv').inputValue as number;
        this.holdTimeInMonth = this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'hold-time').inputValue as number;
        this.estimatedSimoCCPercent = this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'simo-cc').inputValue as number;
        this.estimatedRenovationCosts = this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'reno-cost').inputValue as number;
        this.lessCommissionPercent = this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'less-commission').inputValue as number;
        this.annualPropertyTaxes = this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'property-taxes').inputValue as number;
        this.desiredInvestorROIPercent = this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'desired-roi').inputValue as number;
        this.wholeSaleMarkup = this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'markup').inputValue as number;

        // Show calculator outputs in form
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'simo-cc').outputValue = this.estimatedSimoCC;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'less-closing').outputValue = this.lessClosingCosts;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'less-commission').outputValue = this.lessCommission;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'dispo-cost').outputValue = this.dispositionCosts;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'prop-tax-holding').outputValue = this.propertyTaxesWhileHeld;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'util-insurance-holding').outputValue = this.utilAndInsuranceWhileHeld;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'misc').outputValue = this.misc;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'holding-cost').outputValue = this.holdingCosts;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'profit-margin').outputValue = this.profitMargin;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'front-commission').outputValue = this.frontSideCommission;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'sb-spread').outputValue = this.totalSBSpread;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'second-sale-price').outputValue = this.secondSideSalePrice;
        this.calculator.getField(state.offerCalculator_flipOfferCalculatorBox, 'offer-price').outputValue = this.offerPrice;

        // Save offerPrice on Parse for display elsewhere
        this.state.offerFlip.set('offerPrice', this.offerPrice);
    }

    /**
     * Calculations
     */

    calcEstimatedSimoCC(
        estimatedSimoCCPercent: number,
        holdingCosts: number,
        dispositionCosts: number,
        estimatedRenovationCosts: number,
        arv: number,
        desiredInvestorROIPercent: number
    ): number {
        const temp = 1 + desiredInvestorROIPercent;
        const temp1 = arv / temp;
        const temp2 = temp1 - holdingCosts - dispositionCosts - estimatedRenovationCosts;

        return estimatedSimoCCPercent * temp2;
    }

    calcLessClosingCosts(arv: number): number {
        return arv * 0.02;
    }

    calcLessCommission(arv: number, lessCommissionPercent: number): number {
        return arv * lessCommissionPercent;
    }

    calcDispositionCosts(lessCommissions: number, lessClosingCosts: number): number {
        return lessCommissions + lessClosingCosts;
    }

    calcPropertyTaxesWhileHeld(annualPropertyTaxes: number, holdTimeInMonth: number): number {
        return (annualPropertyTaxes / 12) * holdTimeInMonth;
    }

    calcUtilAndInsuranceWhileHeld(holdTimeInMonth: number): number {
        const insuranceEstimate = this.arv * 0.00035;
        return (250 * holdTimeInMonth) + (insuranceEstimate * holdTimeInMonth);
    }

    calcMisc(estimatedRenovationCosts: number): number {
        return estimatedRenovationCosts * 0.08;
    }

    calcHoldingCosts(propertyTaxesWhileHeld: number, utilAndInsuranceWhileHeld: number, misc: number): number {
        return propertyTaxesWhileHeld + utilAndInsuranceWhileHeld + misc;
    }

    calcProfitMargin(arv: number, desiredInvestorROIPercent: number): number {
        const temp1 = 1 + desiredInvestorROIPercent;
        const temp2 = arv / temp1;

        return arv - temp2;
    }

    calcFrontSideCommission(commissionForSellingAgentPercent: number, offerPrice: number): number {
        return commissionForSellingAgentPercent * offerPrice;
    }

    calcTotalSBSpread(wholeSaleMarkup: number, frontSideCommission: number): number {
        return wholeSaleMarkup + frontSideCommission;
    }

    calcSecondSideSalePrice(
        estimatedSimoCC: number,
        estimatedRenovationCosts: number,
        dispositionCosts: number,
        holdingCosts: number,
        arv: number,
        desiredInvestorROIPercent: number
    ): number {
        const temp0 = 1 + desiredInvestorROIPercent;
        const temp1 = arv / temp0;
        const temp2 = temp1 - estimatedRenovationCosts - dispositionCosts - holdingCosts;

        return temp2 - estimatedSimoCC;
    }

    calcOfferPrice(secondSideSalePrice: number, wholeSaleMarkup: number): number {
        return secondSideSalePrice - wholeSaleMarkup;
    }
}
